import React, { useState } from "react"
import { format, addDays, subDays, subMonths, startOfDay, endOfDay, isAfter, isBefore, isWithinInterval } from "date-fns"
import { Calendar as CalendarIcon, Check } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export interface DateRange {
  from: Date
  to: Date
}

interface DateRangePickerProps {
  value?: DateRange
  onChange?: (range: DateRange) => void
  disabled?: boolean
  placeholder?: string
  className?: string
}

interface DatePickerWithRangeProps {
  date: { from?: Date; to?: Date }
  onDateChange: (date: { from?: Date; to?: Date }) => void
  className?: string
}

const presetRanges = [
  {
    label: "Last 7 days",
    getValue: () => ({
      from: subDays(new Date(), 6),
      to: new Date()
    })
  },
  {
    label: "Last 30 days", 
    getValue: () => ({
      from: subDays(new Date(), 29),
      to: new Date()
    })
  },
  {
    label: "Last 3 months",
    getValue: () => ({
      from: subMonths(new Date(), 3),
      to: new Date()
    })
  },
  {
    label: "Last 6 months",
    getValue: () => ({
      from: subMonths(new Date(), 6),
      to: new Date()
    })
  },
  {
    label: "Last year",
    getValue: () => ({
      from: subMonths(new Date(), 12),
      to: new Date()
    })
  },
  {
    label: "This month",
    getValue: () => {
      const now = new Date()
      const start = new Date(now.getFullYear(), now.getMonth(), 1)
      return {
        from: start,
        to: now
      }
    }
  }
]

export function DateRangePicker({ 
  value, 
  onChange, 
  disabled, 
  placeholder = "Select date range",
  className 
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false)
  const [tempRange, setTempRange] = useState<Partial<DateRange>>(value || {})
  const [selectingStart, setSelectingStart] = useState(true)

  const handleDateSelect = (date: Date | undefined) => {
    if (!date) return

    if (!tempRange.from || (tempRange.from && tempRange.to)) {
      // Starting fresh or restarting selection
      setTempRange({ from: date, to: undefined })
      setSelectingStart(false)
    } else if (tempRange.from && !tempRange.to) {
      // Completing the range
      const newRange = {
        from: tempRange.from,
        to: date
      }
      
      // Ensure from is always before to
      if (isAfter(newRange.from, newRange.to)) {
        newRange.from = date
        newRange.to = tempRange.from
      }
      
      setTempRange(newRange)
      setSelectingStart(true)
    }
  }

  const handlePresetSelect = (preset: DateRange) => {
    setTempRange(preset)
    setSelectingStart(true)
  }

  const handleApply = () => {
    if (tempRange.from && tempRange.to && onChange) {
      onChange({
        from: tempRange.from,
        to: tempRange.to
      })
    }
    setOpen(false)
    setSelectingStart(true)
  }

  const handleCancel = () => {
    setTempRange(value || {})
    setOpen(false)
    setSelectingStart(true)
  }

  const formatDisplayValue = () => {
    if (!value?.from || !value?.to) return placeholder
    return `${format(value.from, 'MMM d, yyyy')} - ${format(value.to, 'MMM d, yyyy')}`
  }

  const isComplete = tempRange.from && tempRange.to
  const isPresetSelected = (preset: any) => {
    if (!tempRange.from || !tempRange.to) return false
    const presetRange = preset.getValue()
    return format(tempRange.from, 'yyyy-MM-dd') === format(presetRange.from, 'yyyy-MM-dd') &&
           format(tempRange.to, 'yyyy-MM-dd') === format(presetRange.to, 'yyyy-MM-dd')
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal h-10 px-4 bg-white hover:bg-slate-50 border-slate-200 hover:border-slate-300 transition-all duration-200",
            !value && "text-muted-foreground",
            value && "text-slate-700",
            className
          )}
          disabled={disabled}
        >
          <CalendarIcon className="mr-2 h-4 w-4 text-slate-500" />
          <span className="truncate">{formatDisplayValue()}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[480px] max-w-[calc(100vw-2rem)] p-0 mx-4" 
        align="start" 
        sideOffset={8}
        avoidCollisions={true}
        collisionPadding={16}
      >
        <div className="flex flex-col sm:flex-row bg-white rounded-lg shadow-lg border border-slate-200 overflow-hidden">
          {/* Compact Preset options */}
          <div className="w-full sm:w-[160px] bg-slate-50/60 border-b sm:border-b-0 sm:border-r border-slate-200 p-3">
            <div className="text-sm font-medium text-slate-600 mb-2">Quick Select</div>
            <div className="grid grid-cols-2 sm:grid-cols-1 gap-0.5 sm:space-y-0.5">
              {presetRanges.map((preset, index) => (
                <button
                  key={index}
                  onClick={() => handlePresetSelect(preset.getValue())}
                  className={cn(
                    "w-full text-left px-2 py-1.5 text-sm rounded transition-all duration-150",
                    "hover:bg-slate-100",
                    isPresetSelected(preset) 
                      ? "bg-blue-50 text-blue-700 font-medium" 
                      : "text-slate-600 hover:text-slate-800"
                  )}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          {/* Compact Calendar Section */}
          <div className="flex-1 p-3 min-w-0">
            {/* Compact Header */}
            <div className="mb-3">
              <div className="text-sm font-medium text-slate-600 mb-1">Custom Range</div>
              <div className="text-sm text-slate-500 bg-slate-50 rounded px-2 py-1">
                {tempRange.from ? (
                  tempRange.to ? (
                    `${format(tempRange.from, 'MMM d')} - ${format(tempRange.to, 'MMM d, yyyy')}`
                  ) : (
                    `${format(tempRange.from, 'MMM d, yyyy')} - Select end`
                  )
                ) : (
                  "Click to select start date"
                )}
              </div>
            </div>
            
            {/* Compact Calendar */}
            <div className="flex justify-center">
              <Calendar
                mode="single"
                selected={tempRange.from || tempRange.to}
                onSelect={handleDateSelect}
                disabled={(date) => date > new Date()}
                className="rounded-md border-0 [&_.rdp-head_cell]:text-sm [&_.rdp-button]:h-8 [&_.rdp-button]:w-8 [&_.rdp-button]:text-sm scale-90 sm:scale-100"
                modifiers={{
                range_start: tempRange.from ? [tempRange.from] : [],
                range_end: tempRange.to ? [tempRange.to] : [],
                range_middle: tempRange.from && tempRange.to ? 
                  Array.from({ length: Math.ceil((tempRange.to.getTime() - tempRange.from.getTime()) / (1000 * 60 * 60 * 24)) - 1 }, (_, i) => 
                    addDays(tempRange.from!, i + 1)
                  ).filter(date => isBefore(date, tempRange.to!)) : []
              }}
              modifiersStyles={{
                range_start: { 
                  backgroundColor: '#3b82f6', 
                  color: 'white',
                  borderRadius: '4px 0 0 4px',
                  fontWeight: '600'
                },
                range_end: { 
                  backgroundColor: '#3b82f6', 
                  color: 'white',
                  borderRadius: '0 4px 4px 0',
                  fontWeight: '600'
                },
                range_middle: { 
                  backgroundColor: '#dbeafe', 
                  color: '#1d4ed8',
                  fontWeight: '500'
                }
              }}
              />
            </div>

            {/* Compact Action buttons */}
            <div className="flex items-center justify-between pt-2 mt-2 border-t border-slate-100">
              <div className="text-sm text-slate-400">
                {isComplete ? 
                  `${Math.ceil((tempRange.to!.getTime() - tempRange.from!.getTime()) / (1000 * 60 * 60 * 24)) + 1} days` : 
                  'Select range'
                }
              </div>
              <div className="flex gap-1.5">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCancel}
                  className="h-8 px-3 text-sm text-slate-600 hover:text-slate-800"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleApply}
                  disabled={!isComplete}
                  className="h-8 px-4 text-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
                >
                  Apply Range
                </Button>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

// Backward compatibility component
export function DatePickerWithRange({
  date,
  onDateChange,
  className,
}: DatePickerWithRangeProps) {
  const value = date.from && date.to ? { from: date.from, to: date.to } : undefined
  
  return (
    <DateRangePicker
      value={value}
      onChange={(range) => onDateChange({ from: range.from, to: range.to })}
      className={className}
    />
  )
}